import { Button, DialogActions, DialogContent, DialogTitle, TunaDialog, useDimensions } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import styles from './create-rider-dialog.module.css'

export type CreateRiderSource = 'latest' | 'members'

export interface CreateRiderDialogProps {
  open: boolean
  /** The band's latest rider; without one the rider is built from members' setups */
  latestName?: string
  /** The rider already made for this gig: the dialog leads with it (duplicate guard) */
  existingName?: string
  /** A create is in flight */
  creating: boolean
  onCreate(source: CreateRiderSource): void
  onOpenExisting(): void
  onClose(): void
}

const latestHelper = 'Copies members, channel order, hospitality and the advance contact.'
const membersHelper = "Builds a new rider from each member's current setup."

/**
 * Create rider (docs/ux/2026-10-16-rider-collaboration-ux.md, "Creating a rider for a gig"): start from the latest
 * rider or from members' setups. When the gig already has a rider, the dialog first says so and offers to open it.
 * Presentational: the host creates, reports and navigates.
 */
export const CreateRiderDialog: FC<CreateRiderDialogProps> = props => {
  const { open, latestName, existingName, creating, onCreate, onOpenExisting, onClose } = props
  const { isSmallOrSmaller } = useDimensions()
  const titleId = useId()
  const latestHelpId = useId()
  const membersHelpId = useId()
  /** Only an explicit pick is stored: the default follows `latestName`, which may arrive after the dialog opens */
  const [picked, setPicked] = useState<CreateRiderSource>()
  const [createAnother, setCreateAnother] = useState(false)
  const guard = Boolean(existingName) && !createAnother
  const chosen: CreateRiderSource = latestName ? (picked ?? 'latest') : 'members'

  const latestRadio = useRef<HTMLInputElement>(null)
  const openRider = useRef<HTMLButtonElement>(null)
  const membersRadio = useRef<HTMLInputElement>(null)

  /** Focus the preselected radio, or Open rider when the guard shows */
  useEffect(() => {
    if (!open) {
      return
    }
    const timer = setTimeout(() => {
      const target = guard ? openRider.current : chosen === 'latest' ? latestRadio.current : membersRadio.current
      target?.focus()
    }, 0)
    return () => clearTimeout(timer)
  }, [open, guard, chosen])

  const radio = (value: CreateRiderSource, label: string, helper: string, helpId: string) => (
    <label className={styles.option}>
      <input
        ref={value === 'latest' ? latestRadio : membersRadio}
        type="radio"
        name={titleId}
        value={value}
        checked={chosen === value}
        aria-labelledby={`${helpId}-label`}
        aria-describedby={helpId}
        onChange={() => setPicked(value)}
      />
      <span className={styles.optionText}>
        <span id={`${helpId}-label`} className={styles.optionLabel}>
          {label}
        </span>
        <span id={helpId} className={styles.helper}>
          {helper}
        </span>
      </span>
    </label>
  )

  return (
    <TunaDialog
      open={open}
      onClose={onClose}
      fullScreen={isSmallOrSmaller}
      maxWidth="sm"
      fullWidth
      aria-labelledby={titleId}
    >
      <DialogTitle id={titleId} onClose={onClose}>
        Create rider
      </DialogTitle>
      <DialogContent>
        {guard ? (
          <p className={styles.guard}>{`${existingName} is already the rider for this gig.`}</p>
        ) : latestName ? (
          <fieldset className={styles.group}>
            <legend className={styles.legend}>Start from</legend>
            {radio('latest', latestName, latestHelper, latestHelpId)}
            {radio('members', "Members' setups", membersHelper, membersHelpId)}
          </fieldset>
        ) : (
          <p className={styles.helper}>{membersHelper}</p>
        )}
      </DialogContent>
      <DialogActions>
        {guard ? (
          <>
            <Button color="inherit" onClick={() => setCreateAnother(true)}>
              Create another
            </Button>
            <Button ref={openRider} variant="contained" onClick={onOpenExisting}>
              Open rider
            </Button>
          </>
        ) : (
          <>
            <Button color="inherit" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="contained" loading={creating} aria-busy={creating} onClick={() => onCreate(chosen)}>
              Create
            </Button>
          </>
        )}
      </DialogActions>
    </TunaDialog>
  )
}

export default CreateRiderDialog
